import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { emailService } from '../services/emailService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  // Start as false — we don't know yet; spinner stays up until first real check
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);
  // Ref to track current user ID without stale closure issues
  const currentUserIdRef = useRef(null);
  // Ref to deduplicate profile checks — store the userId we last completed a check for
  const profileCheckUserIdRef = useRef(null);

  const checkProfileCompleteness = async (userId, { force = false } = {}) => {
    if (!userId) {
      setCheckingProfile(false);
      return;
    }
    // Skip duplicate checks for the same user unless explicitly forced
    if (!force && profileCheckUserIdRef.current === userId) {
      return;
    }
    profileCheckUserIdRef.current = userId;
    try {
      // Only show loading spinner on FIRST load (user not yet in state)
      // Never blank the screen for TOKEN_REFRESHED on tab switch
      if (!user) setCheckingProfile(true);

      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('phone_number, full_name, state, selected_exams')
        .eq('user_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        setCheckingProfile(false);
        return;
      }

      const pendingExamsStr = localStorage.getItem('pendingExams');
      const pendingExams = pendingExamsStr ? JSON.parse(pendingExamsStr) : null;

      if (!profile) {
        const {
          data: { user: currentUser },
        } = await supabase.auth.getUser();
        if (pendingExams) localStorage.removeItem('pendingExams');

        const userEmail = currentUser?.email || session?.user?.email || '';
        const userName =
          currentUser?.user_metadata?.full_name ||
          currentUser?.user_metadata?.name ||
          (userEmail ? userEmail.split('@')[0] : '');

        if (userEmail) {
          await supabase.from('user_profiles').upsert(
            [
              {
                user_id: userId,
                full_name: userName,
                email: userEmail,
                phone_number: currentUser?.user_metadata?.phone_number || '',
                selected_exams: pendingExams || currentUser?.user_metadata?.selected_exams || [],
                auth_provider: currentUser?.app_metadata?.provider || 'google',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              },
            ],
            { onConflict: 'user_id' }
          );
        }

        try {
          if (currentUser?.email) {
            await emailService.sendWelcomeEmail(
              currentUser.email,
              currentUser?.user_metadata?.full_name || ''
            );
          }
        } catch (emailErr) {
          console.error('Failed to send welcome email for Google signup:', emailErr);
        }

        setIsProfileComplete(false);
      } else {
        let isComplete = true;

        if (
          !profile.phone_number ||
          profile.phone_number.trim() === '' ||
          !profile.state ||
          profile.state.trim() === ''
        ) {
          isComplete = false;
        }

        // Check for exam selection
        if (!profile.selected_exams || profile.selected_exams.length === 0) {
          isComplete = false;
        }

        if (pendingExams && pendingExams.length > 0) {
          const currentExams = profile.selected_exams || [];
          const needsUpdate = pendingExams.some((e) => !currentExams.includes(e));

          if (needsUpdate) {
            const {
              data: { user: currentUser },
            } = await supabase.auth.getUser();
            localStorage.removeItem('pendingExams');

            await supabase
              .from('user_profiles')
              .update({
                selected_exams: [...new Set([...currentExams, ...pendingExams])],
                full_name: profile.full_name || currentUser?.user_metadata?.full_name || '',
                auth_provider: 'google',
              })
              .eq('user_id', userId);
          } else {
            localStorage.removeItem('pendingExams');
          }
        }

        setIsProfileComplete(isComplete);
      }
    } catch (err) {
      // console.error('Profile check failed:', err);
    } finally {
      setCheckingProfile(false);
    }
  };

  const getOrCreateDeviceId = () => {
    try {
      const k = 'device_id';
      let v = localStorage.getItem(k);
      if (v) return v;
      v = crypto && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
      localStorage.setItem(k, v);
      return v;
    } catch (e) {
      return 'web';
    }
  };

  // Keep ref in sync with user state so the onAuthStateChange handler always
  // has the current user ID without stale closure issues
  useEffect(() => {
    currentUserIdRef.current = user?.id ?? null;
  }, [user]);

  useEffect(() => {
    const syncSession = async (sess) => {
      setSession((prev) => {
        if (sess?.access_token === prev?.access_token && sess?.user?.id === prev?.user?.id)
          return prev;
        return sess;
      });
      setUser((prev) => {
        if (sess?.user?.id === prev?.id) return prev;
        return sess?.user ?? null;
      });

      if (sess?.user?.id) {
        await checkProfileCompleteness(sess.user.id);
      } else {
        setCheckingProfile(false);
      }
      try {
        localStorage.setItem('sb_session', JSON.stringify(sess || null));
      } catch (e) {
        /* ignore storage errors */
      }
    };

    const recoverFromMirror = async () => {
      try {
        const raw = localStorage.getItem('sb_session');
        if (!raw) return null;
        const cached = JSON.parse(raw);
        if (cached?.access_token && cached?.refresh_token) {
          const { data, error } = await supabase.auth.setSession({
            access_token: cached.access_token,
            refresh_token: cached.refresh_token,
          });
          if (!error) {
            syncSession(data.session);
            return data.session;
          }
        }
      } catch (e) {
        /* ignore recovery errors */
      }
      return null;
    };

    const init = async () => {
      const activeImp = localStorage.getItem('active_impersonation_session');
      if (activeImp) {
        try {
          const rawSess = localStorage.getItem('sb_session');
          if (rawSess) {
            const impSess = JSON.parse(rawSess);
            if (impSess && impSess.user) {
              setSession(impSess);
              setUser(impSess.user);
              checkProfileCompleteness(impSess.user.id);
              setCheckingProfile(false);
              setLoading(false);
              return impSess;
            }
          }
        } catch (e) {
          console.warn('Impersonation session recovery error:', e);
        }
      }

      const {
        data: { session: current },
      } = await supabase.auth.getSession();
      if (current) {
        await syncSession(current);
        setLoading(false);
        return;
      }
      const recovered = await recoverFromMirror();
      setLoading(false);
      if (!recovered) setCheckingProfile(false);
      return recovered;
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'TOKEN_REFRESHED') {
        // Silent token renewal — just update tokens, never trigger loading/profile check
        setSession(session);
        setUser((prev) => (prev?.id === session?.user?.id ? prev : (session?.user ?? null)));
        try {
          localStorage.setItem('sb_session', JSON.stringify(session || null));
        } catch (e) {
          /* ignore */
        }
        return;
      }

      if (
        event === 'SIGNED_IN' &&
        session?.user?.id &&
        session.user.id === currentUserIdRef.current
      ) {
        // Supabase fires SIGNED_IN on EVERY tab re-focus for the same already-logged-in user.
        // This is NOT a real new sign-in — treat it silently, no loading states, no profile re-check.
        setSession(session);
        try {
          localStorage.setItem('sb_session', JSON.stringify(session || null));
        } catch (e) {
          /* ignore */
        }
        return;
      }

      syncSession(session);
      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        setLoading(false);
        if (event === 'SIGNED_OUT') setCheckingProfile(false);
      }

      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user?.id) {
        const existingSessionId = localStorage.getItem('x_session_id');
        if (!existingSessionId) {
          try {
            const deviceId = getOrCreateDeviceId();
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 3000);

            const createSession = async (retryCount = 0) => {
              try {
                const res = await fetch(
                  `${process.env.REACT_APP_SERVER_BASE_URL || ''}/api/session/start`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user_id: session.user.id, device_id: deviceId }),
                    signal: controller.signal,
                  }
                );

                if (res.ok) {
                  const payload = await res.json();
                  if (payload?.session_id) {
                    setSessionId(payload.session_id);
                    try {
                      localStorage.setItem('x_session_id', payload.session_id);
                    } catch (e) {
                      /* storage error */
                    }
                    return true;
                  }
                }
                return false;
              } catch (e) {
                if (retryCount < 2 && !controller.signal.aborted) {
                  await new Promise((resolve) => setTimeout(resolve, 1000));
                  return createSession(retryCount + 1);
                }
                return false;
              }
            };

            createSession().finally(() => clearTimeout(timeout));
          } catch (e) {
            /* session error */
          }
        }
      }
    });

    init();
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;
    let lastValidation = Date.now();
    let consecutiveFailures = 0;
    const MAX_CONSECUTIVE_FAILURES = 8;
    let isInTestMode = false;

    const checkTestMode = () => {
      const currentPath = window.location.pathname;
      const testPaths = [
        '/test',
        '/mock-test',
        '/topic-wise',
        '/chapter-wise',
        '/section-wise',
        '/practice',
        '/quiz',
      ];
      return testPaths.some((path) => currentPath.includes(path));
    };

    const validate = async () => {
      try {
        if (checkTestMode()) {
          isInTestMode = true;
          return;
        }

        const uid = user?.id || (await supabase.auth.getUser()).data?.user?.id;
        if (!uid) return;
        const sid = localStorage.getItem('x_session_id');
        if (!sid) return;

        const base = process.env.REACT_APP_SERVER_BASE_URL || '';
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);

        const res = await fetch(`${base}/api/session/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-session-id': sid },
          body: JSON.stringify({ user_id: uid }),
          signal: controller.signal,
        });

        clearTimeout(timeout);

        if (res.ok) {
          const response = await res.json();
          if (!response.valid) {
            consecutiveFailures++;
            if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES && !cancelled) {
              await signOut();
            }
          } else {
            consecutiveFailures = 0;
            lastValidation = Date.now();

            try {
              const {
                data: { session: currentSession },
              } = await supabase.auth.getSession();
              if (currentSession && currentSession.expires_at) {
                const expiresAt = new Date(currentSession.expires_at * 1000);
                const now = new Date();
                const timeUntilExpiry = expiresAt.getTime() - now.getTime();

                if (timeUntilExpiry < 60 * 60 * 1000) {
                  await supabase.auth.refreshSession();
                }
              }
            } catch (refreshError) {
              /* ignore */
            }
          }
        } else {
          consecutiveFailures++;
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES && !cancelled) {
            await signOut();
          }
        }
      } catch (e) {
        consecutiveFailures++;
        if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES && !cancelled) {
          await signOut();
        }
      }
    };

    const initialTimeout = setTimeout(() => {
      if (!cancelled) validate();
    }, 5000);

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !isInTestMode) {
        if (Date.now() - lastValidation > 300000) {
          validate();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const interval = setInterval(
      () => {
        if (!cancelled && !isInTestMode) validate();
      },
      5 * 60 * 1000
    );

    return () => {
      cancelled = true;
      clearTimeout(initialTimeout);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [user?.id]);

  const signup = async (email, password, userData) => {
    setLoading(true);
    try {
      const { data: userCheck, error: checkError } = await supabase.rpc('user_email_exists', {
        email_address: email,
      });
      if (!checkError && userCheck?.exists) throw new Error('Account already exists.');

      const selected_exams = Array.isArray(userData.selected_exams)
        ? userData.selected_exams.filter((id) =>
            /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)
          )
        : [];

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: userData.full_name || '',
            phone_number: userData.phone_number || '',
            selected_exams,
            utm_source: userData.utm_source,
            utm_medium: userData.utm_medium,
            utm_campaign: userData.utm_campaign,
          },
          emailRedirectTo: `${window.location.origin}/register/verify-email`,
        },
      });
      if (error) throw error;
      return data;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      return data;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/dashboard` },
      });
      if (error) throw error;
      return data;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      await supabase.auth.signOut();
      setSessionId(null);
      localStorage.removeItem('x_session_id');
    } finally {
      setLoading(false);
    }
  };

  const createUserProfile = async (userData) => {
    const {
      data: { user: currentUser },
    } = await supabase.auth.getUser();
    if (!currentUser) throw new Error('Not authenticated');
    const userEmail = currentUser.email || userData.email || '';
    const userName =
      userData.full_name ||
      currentUser.user_metadata?.full_name ||
      currentUser.user_metadata?.name ||
      (userEmail ? userEmail.split('@')[0] : '');
    const { data, error } = await supabase
      .from('user_profiles')
      .upsert(
        [
          {
            user_id: currentUser.id,
            full_name: userName,
            phone_number: userData.phone_number || null,
            selected_exams: userData.selected_exams || [],
            email: userEmail,
            auth_provider: currentUser.app_metadata?.provider || 'email',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        ],
        { onConflict: 'user_id' }
      )
      .select()
      .single();
    if (error && error.code !== '23505') throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    sessionId,
    isProfileComplete,
    checkingProfile,
    checkProfileCompleteness,
    signup,
    login,
    loginWithGoogle,
    signOut,
    createUserProfile,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
